// Pixar Whiteboard Ad's takes (live's `clip-takes`, `clip-take-list` and `take-row`; not captured,
// live's stage needs a paid shoot, so built from live's code, spec G14-pixar-whiteboard-ad PR h):
// each clip's takes, the picked one marked, New take, and each take's length and word check.
import { ActionIcon, Button, Group, Loader, Radio, Stack, Text, Tooltip } from "@mantine/core";
import { Play, RefreshCw } from "lucide-react";
import { useState } from "react";

import type {
  PixarWhiteboardClipTakes,
  PixarWhiteboardTake,
  PixarWhiteboardTakeClip,
  WhiteboardVerdict,
} from "../../../api/generated/model";
import { MediaPreview } from "../../../platform/media/MediaPreview";
import { FieldLabel } from "../../../ui/FieldLabel";
import classes from "./podcast.module.css";
import whiteboard from "./whiteboard.module.css";

/** Live's words (`constants.ts`, `messages.ts`). */
const TAKES_TITLE = "Takes";
const TAKES_TOOLTIP =
  "Every clip shot, measured and checked against its lines. The best take is picked; change it, " +
  "or shoot a new take, then generate the video.";
const CLIP_LABELS: Readonly<Record<PixarWhiteboardTakeClip, string>> = {
  A: "Clip A",
  B: "Clip B",
  C: "Clip C",
  end_card: "End card · silent",
};
const NEW_TAKE = "New take";
const PLAY_TAKE = "Play the take";
const TAKE_RUNNING = "Shooting…";
const UNCHECKED = "Unchecked";
const VERDICTS: Readonly<Record<WhiteboardVerdict, string>> = {
  pass: "Pass",
  trim: "Pass, trimmed",
  check: "Check",
  retake: "Retake",
};

/** A take's line (live's `meta`, less the audio rate, spec decision 15): "14.2 s · 2 cues off". */
export function takeMeta(take: Pick<PixarWhiteboardTake, "duration_s" | "flagged_cues">): string {
  return [
    ...(take.duration_s === null ? [] : [`${Number(take.duration_s.toFixed(1))} s`]),
    ...(take.flagged_cues > 0 ? [`${take.flagged_cues} cue${take.flagged_cues === 1 ? "" : "s"} off`] : []),
  ].join(" · ");
}

interface TakeRowProps {
  clip: PixarWhiteboardTakeClip;
  take: PixarWhiteboardTake;
  picked: boolean;
  disabled: boolean;
  onPick: () => void;
  onPreview: () => void;
}

/** One take: while shot, its loader; then its radio (a finished take only), its label, ▶, its line,
 * its word check (the end card has none) and why it failed. */
function TakeRow({ clip, take, picked, disabled, onPick, onPreview }: TakeRowProps) {
  const label = `Take ${take.number}`;
  if (take.status === "working") {
    return (
      <Group gap={8} className={whiteboard.takeRow}>
        <Loader size="xs" aria-hidden />
        <Text className={whiteboard.takeLabel}>{label}</Text>
        <Text className={whiteboard.note}>{TAKE_RUNNING}</Text>
      </Group>
    );
  }
  const done = take.status === "done";
  return (
    <Group gap={8} wrap="nowrap" className={whiteboard.takeRow} data-picked={picked || undefined}>
      <Radio
        checked={picked}
        disabled={disabled || !done}
        aria-label={`${CLIP_LABELS[clip]}, ${label}`}
        onChange={onPick}
      />
      <Text className={whiteboard.takeLabel}>{label}</Text>
      {done && take.url !== null ? (
        <Tooltip label={PLAY_TAKE} withArrow>
          <ActionIcon variant="subtle" size="sm" aria-label={`${PLAY_TAKE}: ${CLIP_LABELS[clip]}, ${label}`} onClick={onPreview}>
            <Play size={14} aria-hidden />
          </ActionIcon>
        </Tooltip>
      ) : null}
      <Text className={whiteboard.note}>{takeMeta(take)}</Text>
      {clip === "end_card" || !done ? null : (
        <Text className={whiteboard.verdict} data-verdict={take.verdict ?? "none"}>
          {take.verdict === null ? UNCHECKED : VERDICTS[take.verdict]}
        </Text>
      )}
      {take.check_problem === null ? null : <Text className={whiteboard.note}>{take.check_problem}</Text>}
      {take.problem === null ? null : (
        <Text className={classes.fieldError} role="alert">
          {take.problem}
        </Text>
      )}
    </Group>
  );
}

export interface WhiteboardTakesProps {
  clips: readonly PixarWhiteboardClipTakes[];
  /** A take is shot or heard, or the draft asks: nothing is picked or shot (live's `isBusy`). */
  busy: boolean;
  onPick: (clip: PixarWhiteboardTakeClip, take: number) => void;
  onNewTake: (clip: PixarWhiteboardTakeClip) => void;
}

/** Live's "Takes": each clip with New take and a row a take, the end card's one silent take beside
 * them; ▶ plays a take in the shared lightbox. */
export function WhiteboardTakes({ clips, busy, onPick, onNewTake }: WhiteboardTakesProps) {
  const [preview, setPreview] = useState<{ id: string; url: string } | null>(null);
  return (
    <Stack gap={12} aria-label={TAKES_TITLE} component="section">
      <Text component="div" className={classes.castTitle}>
        <FieldLabel label={TAKES_TITLE} info={TAKES_TOOLTIP} spoken />
      </Text>
      {clips.map((row) => (
        <Stack key={row.clip} gap={4}>
          <Group gap={8} justify="space-between">
            <Text className={whiteboard.takeClip}>{CLIP_LABELS[row.clip]}</Text>
            <Button
              variant="subtle"
              size="compact-xs"
              leftSection={<RefreshCw size={14} aria-hidden />}
              aria-label={`${NEW_TAKE}: ${CLIP_LABELS[row.clip]}`}
              disabled={busy}
              onClick={() => onNewTake(row.clip)}
            >
              {NEW_TAKE}
            </Button>
          </Group>
          {row.takes.map((take) => (
            <TakeRow
              key={take.number}
              clip={row.clip}
              take={take}
              picked={row.picked === take.number}
              disabled={busy}
              onPick={() => onPick(row.clip, take.number)}
              onPreview={() => {
                if (take.url !== null) setPreview({ id: `${row.clip}-${take.number}`, url: take.url });
              }}
            />
          ))}
        </Stack>
      ))}
      <MediaPreview
        items={
          preview === null
            ? []
            : [
                {
                  id: preview.id,
                  name: "Take",
                  kind: "video",
                  url: preview.url,
                  thumb: null,
                  poster: null,
                  durationMs: null,
                  width: null,
                  height: null,
                },
              ]
        }
        current={preview?.id ?? null}
        onShow={(id) => {
          if (id === null) setPreview(null);
        }}
      />
    </Stack>
  );
}
